'use client';

import { hashKey, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useRef } from 'react';
import { RegistryContext } from '../context/RegistryContext.js';
import { isUserPortfolioSnapshotWire } from '../internal/profile-portfolio/types.js';
import { useUserPortfolioSnapshotRealtime } from '../internal/profile-portfolio/use-profile-portfolio-realtime.js';
import { createHook } from './createHook.js';
/**
 * The profile snapshot's subjects are derived from its own response (held
 * markets and mints) and recover through its `recovery.streamSequence`
 * anchor, so the realtime owner is hand-written rather than a manifest recipe.
 */
export function createProfilePortfolioSnapshotHook(spec) {
  const keyFactory = spec.entityKeys[spec.kind];
  if (keyFactory === undefined) {
    throw new Error('Profile portfolio snapshot hook requires its query-key factory');
  }
  const useSnapshotQuery = createHook(spec);
  return function useProfilePortfolioSnapshot(args, {
    enabled = true,
    knownWallets = [],
    logger,
    onEvidence
  } = {}) {
    const realtimeAvailable = useContext(RegistryContext) !== null;
    const queryKey = keyFactory(args).queryKey;
    const query = useSnapshotQuery(args, {
      enabled: enabled && realtimeAvailable,
      refetchInterval: false
    });
    const cached = query.data;
    const snapshot = isUserPortfolioSnapshotWire(cached) ? cached : null;
    const realtime = useUserPortfolioSnapshotRealtime({
      enabled,
      logger,
      onEvidence,
      knownWallets: stableWallets(knownWallets),
      snapshot,
      queryKey
    });
    useRejectNonWireSnapshot(queryKey, cached, snapshot, query.refetch, logger);
    return {
      snapshot,
      isError: query.isError,
      isFetching: query.isFetching,
      isRealtimeReady: realtime.isReady,
      async refetch() {
        const result = await query.refetch({
          cancelRefetch: false
        });
        const value = result.data;
        if (result.isError || !isUserPortfolioSnapshotWire(value)) {
          logger?.warn('profile_valuation.explicit_refresh_failed', {
            reason: result.isError ? 'request_failed' : 'non_wire_snapshot'
          });
          return null;
        }
        return value;
      }
    };
  };
}
/** A persisted legacy or malformed value must not stand in for the wire DTO. */
function useRejectNonWireSnapshot(queryKey, cached, snapshot, refetch, logger) {
  const queryClient = useQueryClient();
  const queryHash = hashKey(queryKey);
  const rejectedQueryHash = useRef(null);
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;
  const invalid = cached !== undefined && cached !== null && snapshot === null;
  useEffect(() => {
    if (!invalid) {
      if (snapshot !== null && rejectedQueryHash.current === queryHash) {
        rejectedQueryHash.current = null;
      }
      return;
    }
    if (rejectedQueryHash.current === queryHash) return;
    rejectedQueryHash.current = queryHash;
    logger?.warn('profile_valuation.persisted_cache_rejected', {
      reason: 'legacy_or_non_wire_snapshot'
    });
    queryClient.removeQueries({
      queryKey: queryKeyRef.current,
      exact: true
    }); // ALLOW-STATE-OWNERSHIP: exact rejected snapshot
    void refetch();
  }, [invalid, logger, queryClient, queryHash, refetch, snapshot]);
}
function stableWallets(wallets) {
  return [...new Set(wallets)].sort();
}